// Bring a player's already-claimed precon deck rows up to the current
// progression decklist.
//
// Claiming a progression deck does two things: it grants the cards, and it
// writes a saved-deck row holding that list. The decks were upgraded from the
// printed 36/16 precon to a Constructed-legal 60/30, and the server-side
// `upgraded-starter-deck-backfill` migration hands existing owners the cards
// that were added. It cannot touch their saved-deck row, though, so without
// this pass a returning player owns every card yet still has a 52-card deck
// that canPlayDeck now rejects at matchmaking.
//
// Only untouched rows are rewritten. A row qualifies when its cards are a
// sub-multiset of the current list, which is true for a deck straight from a
// claim (the upgrade was purely additive) and for one the player only removed
// cards from. The moment they add anything of their own, the row stops
// matching and is left alone. Self-limiting: after the rewrite the row is the
// full list, so it fails the below-minimum precondition on every later pass.

import { PROGRESSION_DECKS, resolveStarterDeck } from './starterDecks';
import { MIN_SPELLBOOK_SIZE, MIN_ATLAS_SIZE } from '../sorcery/deckRules';

function countByCardId(cards) {
  const counts = new Map();
  for (const c of cards) counts.set(c.cardId, (counts.get(c.cardId) || 0) + 1);
  return counts;
}

function isSubMultiset(subset, superset) {
  for (const [cardId, qty] of subset) {
    if (qty > (superset.get(cardId) || 0)) return false;
  }
  return true;
}

function isBelowLegalSize(cards, cardTypeOf) {
  let spellbook = 0;
  let atlas = 0;
  for (const entry of cards) {
    const type = cardTypeOf(entry.cardId);
    if (type === 'Avatar' || entry.isSideboard) continue;
    if (type === 'Site') atlas += 1;
    else spellbook += 1;
  }
  return spellbook < MIN_SPELLBOOK_SIZE || atlas < MIN_ATLAS_SIZE;
}

/**
 * Find saved precon decks that are stale relative to the current lists.
 *
 * @param {Array} savedDecks Rows from listSavedDecks
 * @param {Array} sorceryCards Normalized card catalog
 * @returns {Array<{ deck: object, cards: Array }>} decks to re-save, each
 *   with the saved-card entries the current decklist resolves to
 */
export function findStalePreconDecks(savedDecks, sorceryCards) {
  if (!savedDecks?.length || !sorceryCards?.length) return [];

  const byId = new Map(sorceryCards.map((c) => [c.unique_id, c]));
  const cardTypeOf = (cardId) => byId.get(cardId)?.type;
  const stale = [];

  for (const deck of savedDecks) {
    if (!deck?.isPrecon || !deck.cards?.length) continue;
    if (!isBelowLegalSize(deck.cards, cardTypeOf)) continue;

    const owned = countByCardId(deck.cards);
    for (const precon of PROGRESSION_DECKS) {
      const resolved = resolveStarterDeck(precon, sorceryCards);
      if (!isSubMultiset(owned, countByCardId(resolved))) continue;
      stale.push({
        deck,
        cards: resolved.map((c) => ({
          cardId: c.cardId,
          cardName: byId.get(c.cardId)?.name || '',
          printingId: c.printingId,
          foiling: c.foiling || 'S',
          isSideboard: c.zone === 'collection',
        })),
      });
      break;
    }
  }

  return stale;
}
